import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosPrivate } from '../hooks/axiosInstance';

// Async thunks
export const createUser = createAsyncThunk(
  'userManagement/createUser',
  async (userData, thunkAPI) => {
    try {
      const response = await axiosPrivate.post('/admin/users', userData);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || { msg: 'Failed to create user' }
      );
    }
  }
);

export const getAllUsers = createAsyncThunk(
  'userManagement/getAllUsers',
  async (params = {}, thunkAPI) => {
    try {
      const { page = 1, limit = 10, role, search } = params;
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(role && role !== 'all' && { role }),
        ...(search && { search })
      });

      const response = await axiosPrivate.get(`/admin/users?${queryParams}`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || { msg: 'Failed to fetch users' }
      );
    }
  }
);

export const getUserById = createAsyncThunk(
  'userManagement/getUserById',
  async (userId, thunkAPI) => {
    try {
      const response = await axiosPrivate.get(`/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || { msg: 'Failed to fetch user' }
      );
    }
  }
);

export const updateUser = createAsyncThunk(
  'userManagement/updateUser',
  async ({ userId, userData }, thunkAPI) => {
    try {
      const response = await axiosPrivate.put(`/admin/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || { msg: 'Failed to update user' }
      );
    }
  }
);

export const deleteUser = createAsyncThunk(
  'userManagement/deleteUser',
  async (userId, thunkAPI) => {
    try {
      const response = await axiosPrivate.delete(`/admin/users/${userId}`);
      return { ...response.data, deletedUserId: userId };
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data || { msg: 'Failed to delete user' }
      );
    }
  }
);

const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState: {
    users: [],
    selectedUser: null,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalUsers: 0,
      limit: 10
    },
    roleCounts: {},
    filters: {
      search: '',
      role: 'all',
      page: 1
    },
    loading: false,
    error: null,
    success: false,
    operationType: null, // 'create', 'update', 'delete'
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
      state.operationType = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
    resetUserManagement: (state) => {
      state.users = [];
      state.selectedUser = null;
      state.pagination = {
        currentPage: 1,
        totalPages: 1,
        totalUsers: 0,
        limit: 10
      };
      state.roleCounts = {};
      state.filters = {
        search: '',
        role: 'all',
        page: 1
      };
      state.loading = false;
      state.error = null;
      state.success = false;
      state.operationType = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create user
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.operationType = 'create';
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        // Add new user to the beginning of the list
        state.users.unshift(action.payload.user);
        // Update pagination
        state.pagination.totalUsers += 1;
        // Update role counts
        const role = action.payload.user.role;
        state.roleCounts[role] = (state.roleCounts[role] || 0) + 1;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.msg || 'Failed to create user';
        state.operationType = null;
      })
      
      // Get all users
      .addCase(getAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.users;
        state.pagination = action.payload.pagination;
        state.roleCounts = action.payload.roleCounts;
        state.error = null;
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.msg || 'Failed to fetch users';
      })
      
      // Get user by ID
      .addCase(getUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUser = action.payload;
        state.error = null;
      })
      .addCase(getUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.msg || 'Failed to fetch user';
      })
      
      // Update user
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.operationType = 'update';
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        // Update user in the list
        const updatedUser = action.payload.user;
        const index = state.users.findIndex(user => user._id === updatedUser._id);
        if (index !== -1) {
          state.users[index] = updatedUser;
        }
        // Update selected user if it's the same
        if (state.selectedUser && state.selectedUser._id === updatedUser._id) {
          state.selectedUser = updatedUser;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.msg || 'Failed to update user';
        state.operationType = null;
      })
      
      // Delete user
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.operationType = 'delete';
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        // Remove user from the list
        const deletedUserId = action.payload.deletedUserId;
        state.users = state.users.filter(user => user._id !== deletedUserId);
        // Update pagination
        state.pagination.totalUsers -= 1;
        // Clear selected user if it was deleted
        if (state.selectedUser && state.selectedUser._id === deletedUserId) {
          state.selectedUser = null;
        }
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.msg || 'Failed to delete user';
        state.operationType = null;
      });
  },
});

export const { 
  clearError, 
  clearSuccess, 
  setFilters, 
  clearSelectedUser, 
  resetUserManagement 
} = userManagementSlice.actions;

export default userManagementSlice.reducer;